// ───────────────────────────────────────────────────────────────────────────
// Global theme — single source of truth for the app's design tokens and the
// light/dark switch. The `data-theme` attribute lives on <html> so EVERY DOM
// node (including portals, modals and full-screen loaders that render outside
// the main app shell) picks up the right tokens.
//
// Tokens are injected once at startup (see index.js) so `var(--bg)` resolves
// immediately, before any React component mounts.
// ───────────────────────────────────────────────────────────────────────────

export const THEME_STORAGE_KEY = 'sd-theme';

export const getStoredTheme = () => {
  if (typeof window === 'undefined') return 'dark';
  try {
    return localStorage.getItem(THEME_STORAGE_KEY) || 'dark';
  } catch {
    return 'dark';
  }
};

export const applyTheme = (theme) => {
  if (typeof document === 'undefined') return;
  document.documentElement.setAttribute('data-theme', theme);
  try { localStorage.setItem(THEME_STORAGE_KEY, theme); } catch { /* ignore */ }
};

export const toggleStoredTheme = () => {
  const next = getStoredTheme() === 'light' ? 'dark' : 'light';
  applyTheme(next);
  return next;
};

const THEME_CSS = `
:root {
  --bg: #05050F;
  --surface: #0A0A2C;
  --surface2: #10103A;
  --surface3: #161650;
  --vio: #7B68EE;
  --vio2: #A099E8;
  --acc: #C4B5FD;
  --blue: #2563EB;
  --white: #F0EEFF;
  --muted: #5A5580;
  --muted2: #8E88B5;
  --border: #1E1B50;
  --border2: #2A2660;
  --green: #22C55E;
  --yellow: #EAB308;
  --red: #EF4444;
  --orange: #F97316;
}

/* ── LIGHT MODE ────────────────────────────────────────────────────────────
   Re-maps the colour tokens. Higher specificity than the dark :root, so it
   wins regardless of stylesheet injection order. */
:root[data-theme="light"] {
  --bg: #F4F5FB;
  --surface: #FFFFFF;
  --surface2: #F1F2F9;
  --surface3: #E8E9F4;
  --vio: #6C47FF;
  --vio2: #6C47FF;
  --acc: #7B68EE;
  --blue: #2563EB;
  --white: #1A1633;
  --muted: #908AB0;
  --muted2: #5A5580;
  --border: #E4E6F0;
  --border2: #D3D6E6;
}

/* Hard-coded white-on-dark hovers/tracks that don't read from a token. */
[data-theme="light"] ::-webkit-scrollbar-thumb { background: rgba(0,0,0,0.14); }
[data-theme="light"] ::-webkit-scrollbar-thumb:hover { background: rgba(0,0,0,0.24); }
[data-theme="light"] .sd-nav-item:hover { background: rgba(0,0,0,0.045); color: var(--white); }
[data-theme="light"] .sd-user-card:hover { background: rgba(0,0,0,0.045); }
[data-theme="light"] .sd-user-menu-item:hover { background: rgba(0,0,0,0.05); color: var(--white); }
[data-theme="light"] .sd-user-menu { box-shadow: 0 8px 24px rgba(20,20,60,0.14); }
[data-theme="light"] .sd-usage-track { background: rgba(0,0,0,0.07); }
[data-theme="light"] .sd-filter-tab:hover { background: rgba(0,0,0,0.045); }
[data-theme="light"] .sd-nav-item.active { background: rgba(108,71,255,0.1); }
[data-theme="light"] .sd-quick-btn:hover,
[data-theme="light"] .sd-action-btn:hover,
[data-theme="light"] .sd-icon-btn:hover { background: var(--surface2); color: var(--white); }

/* ── ADMIN CONSOLE (Tailwind, hard-coded dark) ─────────────────────────────
   Scoped to .admin-shell so these remaps never leak into the end-user app.
   Only the solid-dark surfaces and text colours need flipping; the faint
   violet borders/tints read fine on white as-is. */
[data-theme="light"] .admin-shell.bg-\\[\\#05050f\\],
[data-theme="light"] .admin-shell .bg-\\[\\#05050f\\] { background-color: #F4F5FB; }
[data-theme="light"] .admin-shell .bg-\\[\\#0a0a1e\\],
[data-theme="light"] .admin-shell .bg-\\[\\#0a0a1e\\]\\/90,
[data-theme="light"] .admin-shell .bg-\\[\\#0a0a22\\],
[data-theme="light"] .admin-shell .bg-\\[\\#0d0d24\\] { background-color: #FFFFFF; }
[data-theme="light"] .admin-shell .text-white { color: #1A1633; }
[data-theme="light"] .admin-shell .text-gray-100,
[data-theme="light"] .admin-shell .text-gray-200,
[data-theme="light"] .admin-shell .text-gray-300 { color: #2A2550; }
[data-theme="light"] .admin-shell .text-gray-400,
[data-theme="light"] .admin-shell .text-gray-500,
[data-theme="light"] .admin-shell .text-gray-600 { color: #5A5580; }
[data-theme="light"] .admin-shell .text-violet-100,
[data-theme="light"] .admin-shell .text-violet-200,
[data-theme="light"] .admin-shell .text-violet-300 { color: #6C47FF; }
[data-theme="light"] .admin-shell .border-gray-700 { border-color: #D3D6E6; }
[data-theme="light"] .admin-shell .bg-gray-800 { background-color: #E8E9F4; }
`;

export const injectThemeStyles = () => {
  if (typeof document === 'undefined') return;
  if (document.getElementById('sd-theme-tokens')) return;
  const el = document.createElement('style');
  el.id = 'sd-theme-tokens';
  el.textContent = THEME_CSS;
  document.head.appendChild(el);
};
